import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { HeartPulse, Server, AlertOctagon, RotateCcw, Ban } from 'lucide-react'
import { useServiceRegistry, useOrderDlq, useRetryOrderDlqEvent, useDiscardOrderDlqEvent } from '@/hooks/data/useSystemHealth'
import { toast } from '@/ui/Toast'
import { userMessage } from '@/api/errors'

function healthColor(health: string) {
  switch (health) {
    case 'Healthy':
      return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
    case 'Unhealthy':
      return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
    default:
      // YARP reports "Unknown" until the first probe cycle completes — not a failure.
      return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
  }
}

function ServiceRegistrySection() {
  const registry = useServiceRegistry()

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
      <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
        <Server className="h-5 w-5" /> Service registry
      </h2>
      <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
        Live YARP cluster/destination health from the gateway's own active health-checks (probed every 15s) — {registry.data?.discovery ?? 'loading discovery mechanism…'}.
      </p>

      {registry.isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : registry.isError ? (
        <p className="text-sm text-red-600">Failed to load the registry — requires a logged-in manager/admin session.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {(registry.data?.clusters ?? []).map((c) => (
            <div key={c.cluster} className="rounded-lg border border-gray-100 p-3 dark:border-gray-700">
              <p className="mb-2 font-mono text-xs font-medium text-gray-800 dark:text-gray-100">{c.cluster}</p>
              {c.destinations.map((d) => (
                <div key={d.address} className="space-y-1">
                  <p className="truncate text-[11px] text-gray-400" title={d.address}>
                    {d.address}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${healthColor(d.health)}`}>active: {d.health}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${healthColor(d.passive)}`}>passive: {d.passive}</span>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function DlqSection() {
  const [status, setStatus] = useState('')
  const dlq = useOrderDlq(status || undefined)
  const retry = useRetryOrderDlqEvent()
  const discard = useDiscardOrderDlqEvent()

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
      <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
        <AlertOctagon className="h-5 w-5" /> Order queue — dead letters
      </h2>
      <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
        OrderService's failed-event backlog (order.payment-events) — the one real-ecommerce service that consumes a queue today. Retrying re-publishes the original event; discarding marks it permanently resolved.
      </p>

      {dlq.data && (
        <div className="mb-3 flex flex-wrap gap-2">
          {Object.entries(dlq.data.counts).map(([k, v]) => (
            <span key={k} className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200">
              {k}: {v}
            </span>
          ))}
        </div>
      )}

      <select
        value={status}
        onChange={(e) => setStatus(e.target.value)}
        className="mb-3 rounded-lg border border-gray-300 px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
      >
        <option value="">All statuses</option>
        <option value="Pending">Pending</option>
        <option value="Retried">Retried</option>
        <option value="Discarded">Discarded</option>
      </select>

      {dlq.isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : dlq.isError ? (
        <p className="text-sm text-red-600">Failed to load — this endpoint is admin-only.</p>
      ) : !dlq.data || dlq.data.items.length === 0 ? (
        <p className="text-sm text-gray-500">No dead-lettered events{status ? ` with status "${status}"` : ''}. That's a good sign.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="text-left text-xs uppercase text-gray-500 dark:text-gray-400">
              <tr>
                <th className="pb-2 pr-4">Event type</th>
                <th className="pb-2 pr-4">Queue</th>
                <th className="pb-2 pr-4">Kind</th>
                <th className="pb-2 pr-4">Status</th>
                <th className="pb-2 pr-4">Failed at</th>
                <th className="pb-2 pr-4">Retries</th>
                <th className="pb-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {dlq.data.items.map((e) => (
                <tr key={e.id}>
                  <td className="py-2 pr-4 font-mono text-xs text-gray-700 dark:text-gray-300">{e.eventType}</td>
                  <td className="py-2 pr-4 text-gray-600 dark:text-gray-400">{e.queue}</td>
                  <td className="py-2 pr-4 text-gray-600 dark:text-gray-400">{e.kind}</td>
                  <td className="py-2 pr-4">{e.status}</td>
                  <td className="py-2 pr-4 text-xs text-gray-500" title={e.error ?? ''}>
                    {new Date(e.failedAt).toLocaleString()}
                  </td>
                  <td className="py-2 pr-4 text-gray-600 dark:text-gray-400">{e.retryCount}</td>
                  <td className="py-2">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={async () => {
                          try {
                            await retry.mutateAsync(e.id)
                            toast.success('Event re-published')
                          } catch (err) {
                            toast.error(userMessage(err))
                          }
                        }}
                        disabled={e.status === 'Discarded'}
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 disabled:opacity-40"
                        title="Retry"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            await discard.mutateAsync(e.id)
                            toast.success('Event discarded')
                          } catch (err) {
                            toast.error(userMessage(err))
                          }
                        }}
                        disabled={e.status === 'Discarded'}
                        className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 disabled:opacity-40"
                        title="Discard"
                      >
                        <Ban className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function SystemHealthPage() {
  return (
    <div className="p-2 md:p-4 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <HeartPulse className="w-6 h-6" /> System Health
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Real gateway/YARP service health and OrderService's dead-letter backlog — no Prometheus/Grafana is deployed in this system, so this is what's actually observable today.
        </p>
      </div>

      <ServiceRegistrySection />
      <DlqSection />
    </div>
  )
}

export const Route = createFileRoute('/(admin)/system-health/')({ component: SystemHealthPage })
